import { CartLinesUpdateEvent } from '@shopify/events';

class BrCartVariantEditor extends HTMLElement {
  connectedCallback() {
    this.#bindEvents();
    this.refreshAvailability();
    this.syncState();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
  }

  #bindEvents() {
    this.abortController?.abort();

    this.abortController =
      new AbortController();

    const { signal } =
      this.abortController;

    this.addEventListener(
      'change',
      this.handleSelectionChange,
      { signal }
    );

    this.addEventListener(
      'click',
      this.handleClick,
      { signal }
    );
  }


  /* ======================================================
     Live DOM
     ====================================================== */

  get details() {
    return this.querySelector(
      'details'
    );
  }

  get status() {
    return this.querySelector(
      '[data-br-cart-variant-status]'
    );
  }

  get submitButton() {
    return this.querySelector(
      '[data-br-cart-variant-submit]'
    );
  }

  get cancelButton() {
    return this.querySelector(
      '[data-br-cart-variant-cancel]'
    );
  }

  get variantData() {
    return this.querySelector(
      '[data-br-cart-variants]'
    );
  }

  getControls() {
    return Array.from(
      this.querySelectorAll(
        '[data-option-index]'
      )
    ).sort(
      (a, b) =>
        Number(a.dataset.optionIndex) -
        Number(b.dataset.optionIndex)
    );
  }

  getVariants() {
    const variantData =
      this.variantData;

    if (!variantData) {
      return [];
    }

    try {
      return JSON.parse(
        variantData.textContent || '[]'
      );
    } catch (error) {
      console.error(
        '[br-cart-variant-editor] Invalid variant data.',
        error
      );

      return [];
    }
  }

  getCartLineKey() {
    return (
      this.closest(
        '.cart-items__table-row'
      )?.dataset.key || ''
    );
  }

  getSectionsToUpdate() {
    const sections =
      new Set();

    for (
      const component of
      document.querySelectorAll(
        'cart-items-component[data-section-id]'
      )
    ) {
      if (
        component instanceof
        HTMLElement &&
        component.dataset.sectionId
      ) {
        sections.add(
          component.dataset.sectionId
        );
      }
    }

    /*
     * Variant editor is drawer-only,
     * but keep a safe fallback.
     */
    if (sections.size === 0) {
      sections.add(
        'cart-drawer-section'
      );
    }

    return Array.from(sections);
  }


  /* ======================================================
     Events
     ====================================================== */

  handleSelectionChange = (event) => {
    if (
      !(event.target instanceof Element)
    ) {
      return;
    }

    const control =
      event.target.closest(
        '[data-option-index]'
      );

    if (
      !control ||
      !this.contains(control)
    ) {
      return;
    }

    this.refreshAvailability();
    this.syncState();
  };


  handleClick = (event) => {
    if (
      !(event.target instanceof Element)
    ) {
      return;
    }

    const updateButton =
      event.target.closest(
        '[data-br-cart-variant-submit]'
      );

    if (
      updateButton &&
      this.contains(updateButton)
    ) {
      event.preventDefault();

      if (
        updateButton.disabled ||
        this.hasAttribute('aria-busy')
      ) {
        return;
      }

      void this.handleUpdate();

      return;
    }

    const cancelButton =
      event.target.closest(
        '[data-br-cart-variant-cancel]'
      );

    if (
      cancelButton &&
      this.contains(cancelButton)
    ) {
      event.preventDefault();

      this.resetSelection();

      this.details?.removeAttribute(
        'open'
      );

      return;
    }

    const summary =
      event.target.closest(
        '.br-cart-variant-editor__summary'
      );

    if (
      summary &&
      this.contains(summary)
    ) {
      requestAnimationFrame(() => {
        if (
          !this.isConnected ||
          !this.details?.open
        ) {
          return;
        }

        this.refreshAvailability();
        this.syncState();
      });
    }
  };


  /* ======================================================
     Cart Variant replacement
     ====================================================== */

  handleUpdate = async () => {
    const variant =
      this.getSelectedVariant();

    if (!variant?.available) {
      this.setStatus(
        'This combination is sold out.',
        true
      );

      return;
    }

    if (
      String(variant.id) ===
      String(
        this.dataset.currentVariantId
      )
    ) {
      this.details?.removeAttribute(
        'open'
      );

      return;
    }

    const lineKey =
      this.getCartLineKey();

    if (!lineKey) {
      this.setStatus(
        'Unable to identify this cart item. Please refresh and try again.',
        true
      );

      return;
    }

    const root =
      window.Shopify?.routes?.root;

    if (!root) {
      this.setStatus(
        'Cart options could not be updated. Please try again.',
        true
      );

      return;
    }

    this.setBusy(true);
    this.setStatus('Updating…');

    try {
      /*
       * Read the live Ajax cart first.
       *
       * Ajax cart items expose exactly what we need:
       * - line item key
       * - variant_id
       * - quantity
       * - properties
       */
      const cartResponse =
        await fetch(
          `${root}cart.js`,
          {
            headers: {
              Accept:
                'application/json',
            },
            credentials:
              'same-origin',
          }
        );

      if (!cartResponse.ok) {
        throw new Error(
          'Unable to read the current cart.'
        );
      }

      const cart =
        await cartResponse.json();

      const currentItem =
        cart.items?.find(
          (item) =>
            item.key === lineKey
        );

      if (!currentItem) {
        throw new Error(
          'Unable to identify this cart item. Please refresh and try again.'
        );
      }

      /*
       * This editor is intentionally not rendered
       * for property-bearing lines.
       *
       * Recheck before mutation in case cart state
       * changed after the section rendered.
       */
      if (
        Object.keys(
          currentItem.properties || {}
        ).length > 0
      ) {
        throw new Error(
          'This item has custom options and can’t be changed here.'
        );
      }

      const targetVariantId =
        String(variant.id);

      /*
       * Don't let cart/update.js ambiguously merge
       * into another existing line that already uses
       * this Variant.
       *
       * This keeps line identity and inventory behavior
       * predictable.
       */
      const quantity =
        Number.parseInt(
            currentItem.quantity,
            10
        );

        const safeQuantity =
        Number.isFinite(quantity) &&
        quantity > 0
            ? quantity
            : 1;


        /*
        * Look for an existing standard cart line
        * that already uses the selected Variant.
        *
        * Only merge into a plain line:
        * - no line-item properties
        * - no selling plan
        *
        * This avoids accidentally merging a standard
        * product into a customized/subscription line.
        */
        const existingTarget =
        cart.items?.find(
            (item) =>
            item.key !==
                currentItem.key &&
            String(
                item.variant_id
            ) === targetVariantId &&
            Object.keys(
                item.properties || {}
            ).length === 0 &&
            !item.selling_plan_allocation
        );


        /*
        * Also detect the same Variant when it exists
        * only as a customized/subscription line.
        *
        * In that case we must not target the Variant ID
        * through cart/update.js because Shopify could
        * match the wrong line.
        */
        const conflictingTarget =
        cart.items?.find(
            (item) =>
            item.key !==
                currentItem.key &&
            String(
                item.variant_id
            ) === targetVariantId &&
            (
                Object.keys(
                item.properties || {}
                ).length > 0 ||
                Boolean(
                item.selling_plan_allocation
                )
            )
        );


        if (
        conflictingTarget &&
        !existingTarget
        ) {
        throw new Error(
            'This option is already in your cart with custom purchase details and can’t be merged automatically.'
        );
        }


        let updates;
        let eventLines;


        /*
        * Case A
        *
        * Target Variant already exists as a normal
        * cart line.
        *
        * Example:
        *
        * XS × 2
        * S  × 1
        *
        * Change XS -> S
        *
        * Result:
        *
        * S × 3
        */
        if (existingTarget) {
        const existingQuantity =
            Number.parseInt(
            existingTarget.quantity,
            10
            );

        const safeExistingQuantity =
            Number.isFinite(
            existingQuantity
            ) &&
            existingQuantity > 0
            ? existingQuantity
            : 0;

        const mergedQuantity =
            safeExistingQuantity +
            safeQuantity;

        updates = {
            [currentItem.key]: 0,
            [existingTarget.key]:
            mergedQuantity,
        };

        eventLines = [
            {
            id:
                currentItem.key,

            quantity:
                0,
            },

            {
            id:
                existingTarget.key,

            quantity:
                mergedQuantity,
            },
        ];

        }


        /*
        * Case B
        *
        * Target Variant isn't already in cart.
        *
        * Remove the current exact line and let
        * cart/update.js create the selected Variant.
        */
        else {
        updates = {
            [currentItem.key]: 0,
            [targetVariantId]:
            safeQuantity,
        };

        eventLines = [
            {
            id:
                currentItem.key,

            quantity:
                0,
            },

            {
            merchandiseId:
                `gid://shopify/ProductVariant/${targetVariantId}`,

            quantity:
                safeQuantity,
            },
        ];
        }

      const sections =
        this.getSectionsToUpdate();

      const body = {
        updates,

        sections:
          sections.join(','),

        sections_url:
          window.location.pathname,
      };

      const updatePromise =
        fetch(
          `${root}cart/update.js`,
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              Accept:
                'application/json',
            },

            credentials:
              'same-origin',

            body:
              JSON.stringify(body),
          }
        )
          .then(
            async (response) => {
              const data =
                await response.json();

              if (
                !response.ok ||
                data.errors
              ) {
                const message =
                  typeof data.errors ===
                  'string'
                    ? data.errors
                    : data.description ||
                      data.message ||
                      'Unable to update this option.';

                throw new Error(
                  message
                );
              }

              return {
                cart:
                  CartLinesUpdateEvent
                    .createCartFromAjaxResponse(
                      data
                    ),

                detail: {
                  sections:
                    data.sections,

                  items:
                    data.items,

                  itemCount:
                    data.item_count,

                  source:
                    'br-cart-variant-editor',

                  didError:
                    false,
                },
              };
            }
          );

      /*
       * Let Horizon handle:
       * - cart drawer morph
       * - cart bubble
       * - totals
       * - discounts
       * - Buy 3 UI
       * - Sale Picks
       *
       * Don't manually replace drawer HTML.
       */
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'update',

          context: 'cart',

          lines: eventLines,

          promise:
            updatePromise,
        })
      );

      await updatePromise;

    } catch (error) {
      console.error(
        '[br-cart-variant-editor] Update failed.',
        error
      );

      this.setStatus(
        error?.message ||
          'Unable to update this option. Please try again.',
        true
      );

    } finally {
      if (this.isConnected) {
        this.setBusy(false);
      }
    }
  };


  /* ======================================================
     Variant resolution
     ====================================================== */

  getSelectedValues(
    controls = this.getControls()
  ) {
    return controls.map(
      (control) =>
        control.value
    );
  }


  getSelectedVariant(
    controls = this.getControls()
  ) {
    const selectedValues =
      this.getSelectedValues(
        controls
      );

    return this.getVariants().find(
      (variant) =>
        Array.isArray(
          variant.options
        ) &&
        variant.options.length ===
          selectedValues.length &&
        variant.options.every(
          (value, index) =>
            value ===
            selectedValues[index]
        )
    );
  }


  /* ======================================================
     Inventory / option availability
     ====================================================== */

  refreshAvailability() {
    const controls =
      this.getControls();

    const variants =
      this.getVariants();

    for (
      const control of
      controls
    ) {
      if (
        !(
          control instanceof
          HTMLSelectElement
        )
      ) {
        continue;
      }

      const optionIndex =
        Number(
          control.dataset.optionIndex
        );

      for (
        const option of
        control.options
      ) {
        if (
          !option.dataset.baseLabel
        ) {
          option.dataset.baseLabel =
            option.textContent
              .replace(
                /\s+—\s+Sold out$/i,
                ''
              )
              .trim();
        }

        const isAvailable =
          this.hasAvailableVariantForOption(
            optionIndex,
            option.value,
            controls,
            variants
          );

        option.disabled =
          !isAvailable;

        option.hidden =
          !isAvailable;

        option.textContent =
          option.dataset.baseLabel;
      }

      const selectedOption =
        control.selectedOptions[0];

      if (
        selectedOption?.disabled ||
        selectedOption?.hidden
      ) {
        const firstAvailable =
          Array.from(
            control.options
          ).find(
            (option) =>
              !option.disabled &&
              !option.hidden
          );

        if (firstAvailable) {
          control.value =
            firstAvailable.value;
        }
      }
    }
  }


  hasAvailableVariantForOption(
    optionIndex,
    candidateValue,
    controls = this.getControls(),
    variants = this.getVariants()
  ) {
    return variants.some(
      (variant) => {
        if (
          !variant.available ||
          !Array.isArray(
            variant.options
          )
        ) {
          return false;
        }

        if (
          variant.options[
            optionIndex
          ] !== candidateValue
        ) {
          return false;
        }

        for (
          let index = 0;
          index < optionIndex;
          index += 1
        ) {
          if (
            variant.options[index] !==
            controls[index]?.value
          ) {
            return false;
          }
        }

        return true;
      }
    );
  }


  /* ======================================================
     Button state
     ====================================================== */

  syncState() {
    const submitButton =
      this.submitButton;

    if (!submitButton) {
      return;
    }

    const variant =
      this.getSelectedVariant();

    const isCurrentVariant =
      variant &&
      String(variant.id) ===
        String(
          this.dataset.currentVariantId
        );

    submitButton.disabled =
      !Boolean(
        variant?.available &&
        !isCurrentVariant
      );

    if (!variant) {
      this.setStatus(
        'This combination is unavailable.',
        true
      );

      return;
    }

    if (!variant.available) {
      this.setStatus(
        'This combination is sold out.',
        true
      );

      return;
    }

    this.setStatus('');
  }


  resetSelection() {
    const controls =
      this.getControls();

    for (
      const control of
      controls
    ) {
      const initialValue =
        control.dataset.initialValue;

      if (
        initialValue != null
      ) {
        control.value =
          initialValue;
      }
    }

    this.refreshAvailability();
    this.syncState();
  }


  setBusy(isBusy) {
    this.toggleAttribute(
      'aria-busy',
      isBusy
    );

    for (
      const control of
      this.getControls()
    ) {
      control.disabled =
        isBusy;
    }

    const cancelButton =
      this.cancelButton;

    const submitButton =
      this.submitButton;

    if (cancelButton) {
      cancelButton.disabled =
        isBusy;
    }

    if (submitButton) {
      submitButton.disabled =
        true;
    }

    if (!isBusy) {
      this.refreshAvailability();
      this.syncState();
    }
  }


  setStatus(
    message,
    isError = false
  ) {
    const status =
      this.status;

    if (!status) {
      return;
    }

    status.textContent =
      message;

    status.classList.toggle(
      'br-cart-variant-editor__status--error',
      Boolean(
        message &&
        isError
      )
    );
  }
}


if (
  !customElements.get(
    'br-cart-variant-editor'
  )
) {
  customElements.define(
    'br-cart-variant-editor',
    BrCartVariantEditor
  );
}